import { useEffect, useRef, useState } from 'react';
import './Vikili.css';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: string;
}

export const Vikili = () => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationStage, setAnimationStage] = useState('idle');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const positionRef = useRef({ x: -100, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const animationLoop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particlesRef.current = particlesRef.current.filter((p) => p.life > 0);
      particlesRef.current.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vy += 0.2;
        particle.life -= 0.02;

        ctx.fillStyle = particle.color.replace(')', `, ${particle.life})`);
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, 3, 0, Math.PI * 2);
        ctx.fill();
      });

      requestAnimationFrame(animationLoop);
    };

    animationLoop();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  const spawnParticles = (x: number, y: number) => {
    const colors = [
      'rgba(0, 200, 255',
      'rgba(200, 0, 255',
      'rgba(100, 255, 200',
    ];

    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8;
      particlesRef.current.push({
        x,
        y,
        vx: Math.cos(angle) * 3,
        vy: Math.sin(angle) * 3 - 2,
        life: 1,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }
  };

  const playAnimation = async () => {
    if (isAnimating) return;
    setIsAnimating(true);

    const stages = [
      { duration: 1500, stage: 'running' },
      { duration: 500, stage: 'idle' },
      { duration: 800, stage: 'waving' },
      { duration: 2000, stage: 'dancing' },
    ];

    for (const { duration, stage } of stages) {
      setAnimationStage(stage);
      if (stage === 'running') {
        const startX = -100;
        const endX = window.innerWidth / 2 - 40;
        const startTime = Date.now();

        while (Date.now() - startTime < duration) {
          const progress = (Date.now() - startTime) / duration;
          positionRef.current.x = startX + (endX - startX) * progress;
          spawnParticles(
            positionRef.current.x + 40,
            positionRef.current.y + 100
          );
          await new Promise((resolve) => setTimeout(resolve, 16));
        }
        positionRef.current.x = endX;
      } else {
        await new Promise((resolve) => setTimeout(resolve, duration));
      }

      if (stage === 'waving' || stage === 'dancing') {
        for (let i = 0; i < 3; i++) {
          spawnParticles(
            positionRef.current.x + 40,
            positionRef.current.y + 50
          );
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }
    }

    setAnimationStage('idle');
    setIsAnimating(false);
  };

  return (
    <div className="vikili-container">
      <canvas ref={canvasRef} className="particles-canvas" />

      <div className="vikili-stage">
        <div
          className="vikili-body"
          style={{
            transform: `translateX(${positionRef.current.x}px)`,
          }}
        >
          {/* Head */}
          <div className={`vikili-head ${animationStage}`}>
            {/* Screen Face */}
            <div className="vikili-screen">
              <div className={`screen-content ${animationStage}`}>
                {animationStage === 'waving' && (
                  <div className="screen-face happy">
                    <span className="eyes">◉ ◉</span>
                    <span className="mouth">︶</span>
                  </div>
                )}
                {animationStage === 'dancing' && (
                  <div className="screen-face excited">
                    <span className="eyes">◎ ◎</span>
                    <span className="mouth">︵</span>
                  </div>
                )}
                {animationStage === 'running' && (
                  <div className="screen-face neutral">
                    <span className="eyes">○ ○</span>
                    <span className="mouth">−</span>
                  </div>
                )}
                {animationStage === 'idle' && (
                  <div className="screen-face neutral">
                    <span className="eyes blink">◐ ◑</span>
                    <span className="mouth">︶</span>
                  </div>
                )}
              </div>
            </div>

            {/* Neon glow */}
            <div className="neon-glow blue"></div>
            <div className="neon-glow purple"></div>
          </div>

          {/* Body */}
          <div className="vikili-torso">
            <div className="v-letter">V</div>

            {/* Left Arm */}
            <div className={`vikili-arm left ${animationStage}`}>
              <div className="arm-segment upper"></div>
              <div className="arm-segment lower"></div>
              <div className="hand"></div>
            </div>

            {/* Right Arm */}
            <div className={`vikili-arm right ${animationStage}`}>
              <div className="arm-segment upper"></div>
              <div className="arm-segment lower"></div>
              <div className="hand"></div>
            </div>
          </div>

          {/* Legs */}
          <div className={`vikili-legs ${animationStage}`}>
            <div className="leg left">
              <div className="thigh"></div>
              <div className="shin"></div>
              <div className="foot"></div>
            </div>
            <div className="leg right">
              <div className="thigh"></div>
              <div className="shin"></div>
              <div className="foot"></div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={playAnimation}
        disabled={isAnimating}
        className="vikili-button"
      >
        {isAnimating ? 'Викили се движи...' : 'Пусни Викили'}
      </button>
    </div>
  );
};
